import React from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendar,
  faChevronRight,
  faLock,
} from "@fortawesome/free-solid-svg-icons";

/**
 * Format a date into a friendly relative/readable string
 * e.g. "3 months ago", "Jan 2024", "Last week"
 */
function formatFriendlyDate(dateValue) {
  if (!dateValue) return "--";

  const date = new Date(dateValue);
  if (isNaN(date.getTime())) return "--";

  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 1) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 14) return "Last week";
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 60) return "Last month";
  if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return `${months} month${months > 1 ? "s" : ""} ago`;
  }

  // Older than a year — show month + year
  return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

function getRankStyle(rank) {
  switch (rank) {
    case 1:
      return "bg-gradient-to-br from-amber-400 to-amber-600 text-amber-950";
    case 2:
      return "bg-gradient-to-br from-gray-300 to-gray-500 text-gray-900";
    case 3:
      return "bg-gradient-to-br from-amber-600 to-amber-800 text-amber-100";
    default:
      return "bg-gray-100 text-medium-grey";
  }
}

const ArchivedLeagues = ({ leagues = [], userId, onViewHistory }) => {
  const navigate = useNavigate();

  if (leagues.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faLock} className="text-sm text-medium-grey" />
          <h2 className="text-lg font-semibold text-dark-grey">
            Archived Leagues
          </h2>
        </div>
        <p className="text-sm text-medium-grey text-center py-4">
          No archived leagues
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <FontAwesomeIcon icon={faLock} className="text-sm text-medium-grey" />
        <h2 className="text-lg font-semibold text-dark-grey">
          Archived Leagues
        </h2>
      </div>

      <div className="space-y-3">
        {leagues.map((league) => {
          // Determine end date from league data
          const endDate =
            league.endDate || league.updatedAt || league.archivedAt;
          const friendlyEndDate = formatFriendlyDate(endDate);

          return (
            <div
              key={league._id}
              className="bg-white/50 rounded-2xl border border-gray-200 overflow-hidden"
            >
              <div className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-xl flex items-center justify-center ${getRankStyle(
                        league.finalRank || 0,
                      )}`}
                    >
                      <span className="font-bold text-sm">
                        {league.finalRank
                          ? `#${league.finalRank}`
                          : league.leagueName?.charAt(0) || "?"}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-dark-grey">
                        {league.leagueName}
                      </h3>
                      <p className="text-xs text-medium-grey">
                        {league.season ||
                          (league.status === "archived"
                            ? "Archived"
                            : "Inactive")}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-2">
                  <div className="text-center p-2 bg-gray-50 rounded-lg">
                    <p className="text-sm font-bold text-dark-grey">
                      {league.members?.length || league.totalMembers || "--"}
                    </p>
                    <p className="text-[10px] text-medium-grey">Members</p>
                  </div>
                  <div className="text-center p-2 bg-gray-50 rounded-lg">
                    <p className="text-sm font-bold text-dark-grey">
                      {league.numPlayers || "--"}
                    </p>
                    <p className="text-[10px] text-medium-grey">Max Size</p>
                  </div>
                  <div className="text-center p-2 bg-gray-50 rounded-lg">
                    <p className="text-sm font-bold text-dark-grey">
                      {friendlyEndDate}
                    </p>
                    <p className="text-[10px] text-medium-grey">Ended</p>
                  </div>
                </div>

                {/* View History Link */}
                <button
                  onClick={() => {
                    if (onViewHistory) {
                      onViewHistory(league._id);
                    } else {
                      navigate(`/league/${league._id}`);
                    }
                  }}
                  className="mt-3 w-full flex items-center justify-center gap-2 py-2 text-xs text-medium-grey hover:text-dark-grey transition-colors"
                >
                  <FontAwesomeIcon icon={faCalendar} className="text-[10px]" />
                  View Event History
                  <FontAwesomeIcon
                    icon={faChevronRight}
                    className="text-[10px]"
                  />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ArchivedLeagues;
